import { Injectable } from '@angular/core';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { environment } from '../../environments/environment';
import { getAuth, signInWithCustomToken, UserCredential, User, sendEmailVerification, onAuthStateChanged } from 'firebase/auth';
import { getStorage, ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject, FirebaseStorage, UploadResult, UploadTask, UploadTaskSnapshot } from 'firebase/storage';
import { GlobalLoadingService } from './global-loading.service';
import { GlobalMessageService } from './global-message.service';

@Injectable({ providedIn: 'root' })
export class FirebaseService {
  private app: FirebaseApp;
  private storage: FirebaseStorage;

  constructor(
    private globalLoadingService: GlobalLoadingService,
    private globalMessageService: GlobalMessageService,
  ) {
    this.app = initializeApp(environment.FIREBASE_DETAILS);
    this.storage = getStorage(this.app);
  }

  /**
   * Subscribe to user authentication state changes including email verification
   * @param callback Function to call when user state changes
   * @returns Unsubscribe function
   */
  onUserStateChanged(callback: (user: User | null) => void): () => void {
    const auth = getAuth(this.app);
    return onAuthStateChanged(auth, callback);
  }

  getApp(): FirebaseApp {
    return this.app;
  }

  async signInWithCustomToken(token: string): Promise<UserCredential> {
    const auth = getAuth(this.app);
    const userCredential = await signInWithCustomToken(auth, token);
    return userCredential;
  }

  async getIdToken(): Promise<string | null> {
    const auth = getAuth(this.app);
    const user: User | null = auth.currentUser;
    if (!user) return null;
    return await user.getIdToken();
  }

  async sendVerificationEmail() {
    const auth = getAuth(this.app);
    const user = auth.currentUser!;
    console.log('Sending verification email to:', user.email);
    await sendEmailVerification(user);
  }

  async signOut() {
    const auth = getAuth(this.app);
    await auth.signOut();
  }

  async uploadPublicFile(file: File, fileName: string): Promise<string> {
    this.globalLoadingService.show();

    try {
      const fileNameToUse = fileName || file.name;
      const path = `public-uploads/${fileNameToUse}`;
      const storageRef = ref(this.storage, path);

      const uploadResult: UploadResult = await uploadBytes(storageRef, file);
      return path;
    } catch (error: any) {
      console.error('Error uploading public file:', error);
      throw error;
    } finally {
      this.globalLoadingService.hide();
    }
  }

  getStorage(): FirebaseStorage {
    return this.storage;
  }
}
